import { Socket } from 'socket.io-client';
import {
    CLIENT_EMIT_CALL_STATE,
    CLIENT_EMIT_CLAIM_SESSION,
    CLIENT_EMIT_USER_CURRENT_STATUS,
    CLIENT_EMIT_USER_PROFILE
} from 'services/socket-connection/events';

/** Where this tab stands with the realtime server's one-live-session-per-user rule. */
export type TSessionState = 'claiming' | 'active' | 'busy-open-elsewhere' | 'busy-in-call' | 'replaced';

/** 'initial' = page load or "Use here", 'resume' = automatic reconnect, 'force' = the user chose to take over from the other tab or device. */
export type TClaimMode = 'initial' | 'resume' | 'force';

export type TClaimResult =
    | { status: 'granted' }
    | { status: 'busy'; reason: 'in-call' | 'open-elsewhere' }
    | { status: 'replaced' }
    // The server couldn't read the claim; handled like no answer (fail-open).
    | { status: 'invalid' };

/** How long to wait for the server before treating a claim as granted (fail-open keeps today's behaviour). */
export const CLAIM_ACK_TIMEOUT_MS = 5000;

/** Next session state for a claim answer; `undefined` means no answer (timeout, or a server without session claims). */
export const nextStateFromClaim = (result: TClaimResult | undefined): TSessionState => {
    switch (result?.status) {
        case 'busy':
            // An unknown reason still asks the user; only 'in-call' gets the stronger warning.
            return result.reason === 'in-call' ? 'busy-in-call' : 'busy-open-elsewhere';
        case 'replaced':
            return 'replaced';
        default:
            return 'active';
    }
};

/** Only a real server grant counts as having held the session; a timeout or an unknown answer is a fail-open. */
export const isServerGrant = (result: TClaimResult | undefined): boolean => result?.status === 'granted';

/** A request from the takeover screen wins; otherwise a tab that already had the session resumes it, and a new one claims. */
export const claimModeOnConnect = (requestedMode: TClaimMode | null, hasBeenActive: boolean): TClaimMode =>
    requestedMode ?? (hasBeenActive ? 'resume' : 'initial');

let isLocalCallActive = false;
/** Set by the call controller; re-sent after every grant because the server can lose it (restart, recovery). */
export const setLocalCallActive = (inCall: boolean): void => {
    isLocalCallActive = inCall;
};
export const isLocalCallActiveNow = (): boolean => isLocalCallActive;

/**
 * After an explicit "Use here" the user has already chosen: take over without asking again, unless the other session is in a call.
 * Never after a 'force' claim itself, so an unexpected busy answer to it can't loop.
 */
export const shouldForceAfterBusy = (state: TSessionState, isExplicitClaim: boolean, claimMode: TClaimMode): boolean =>
    isExplicitClaim && claimMode !== 'force' && state === 'busy-open-elsewhere';

// Re-sent fresh after every grant (profile, status, call state), or superseded by the next claim.
const EVENTS_RESENT_AFTER_GRANT = new Set<string>([
    CLIENT_EMIT_CLAIM_SESSION,
    CLIENT_EMIT_USER_PROFILE,
    CLIENT_EMIT_USER_CURRENT_STATUS,
    CLIENT_EMIT_CALL_STATE
]);

/**
 * Emits socket.io-client queued while disconnected (its `sendBuffer` packets), to send again once the server grants the claim:
 * sent before the claim they would be dropped. Leaves out what the grant re-sends fresh, and emits waiting for an answer,
 * whose callback is tied to the packet id.
 */
export const emitsToReplayAfterGrant = (packets: { data?: unknown; id?: number }[]): unknown[][] =>
    packets
        .filter((packet) => packet.id === undefined && Array.isArray(packet.data) && !EVENTS_RESENT_AFTER_GRANT.has(packet.data[0]))
        .map((packet) => packet.data as unknown[]);

/** The call-state emit, shared by the call controller and the re-send after a grant. */
export const emitCallState = (socket: Socket, inCall: boolean): void => {
    socket.emit(CLIENT_EMIT_CALL_STATE, { inCall });
};

/** A reload makes the tab claim the session again, so a tab that lost it (or is asking about a call) must not reload on its own. */
export const shouldAutoReloadOnOnline = (state: TSessionState): boolean => state === 'active' || state === 'claiming';

const RESUME_ON_LOAD_KEY = 'sessionResumeOnLoad';

/** A resume marker older than this is stale: a reload that never mounted Campus (e.g. redirected to `/login`) must not affect a later, unrelated login. */
export const RESUME_MARKER_MAX_AGE_MS = 60_000;

/**
 * When a page that holds the session unloads (a refresh, the automatic reload on `online`): carries this tab's own
 * session id through the reload. The old page's socket can still count as live on the server for a moment, so a
 * refreshed tab with a new id would be asked "Campus is already open…" about itself. With the same id, the reloaded
 * page's 'resume' claim is granted by the server's same-sessionId rule, while a tab that really was replaced still
 * gets 'replaced', because its id no longer matches. "Duplicate tab" doesn't unload the original, so it gets no marker.
 */
export const markResumeOnNextLoad = (sessionId: string): void => {
    try {
        sessionStorage.setItem(RESUME_ON_LOAD_KEY, JSON.stringify({ sessionId, at: Date.now() }));
    } catch {
        // storage unavailable
    }
};

/** Reads and clears the marker (one-shot, read at mount before the first claim). */
export const takeResumeSessionId = (now: number = Date.now()): string | null => {
    try {
        const raw = sessionStorage.getItem(RESUME_ON_LOAD_KEY);
        sessionStorage.removeItem(RESUME_ON_LOAD_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed?.sessionId !== 'string' || !parsed.sessionId || typeof parsed?.at !== 'number') return null;
        if (now - parsed.at > RESUME_MARKER_MAX_AGE_MS) return null;
        return parsed.sessionId;
    } catch {
        return null;
    }
};
